/** tm/pages/TaskByTypePage.tsx — 업무별 관리 3컬럼 (카테고리트리3depth|업체카드|TaskStepDetail) */
import { useState, useMemo, type ReactNode } from 'react';
import type { TMStore } from '../hooks';
import type { TaskCategory } from '../types';
import { ceoShown } from '../types';
import { C, T, R, Badge, PBar, DDayBadge, Empty, SearchInput } from '../components/ui';
import { ResizeHandle, useResizable } from '../components/ResizeHandle';
import TaskStepDetail from '../components/TaskStepDetail';
import CompanyDetailPanel from '../components/CompanyDetailPanel';
import * as api from '../api';
import RegisterClientModal from '../components/RegisterClientModal';

// ── 카테고리 트리 빌드 ──
interface CatNode { id:string; name:string; sort_order:number; children:CatNode[] }
function buildTree(cats:TaskCategory[]):CatNode[] {
  const m = new Map<string,CatNode>();
  cats.forEach(c=>m.set(c.id,{id:c.id,name:c.name,sort_order:c.sort_order||0,children:[]}));
  const roots:CatNode[]=[];
  [...cats].sort((a,b)=>(a.sort_order||0)-(b.sort_order||0)).forEach(c=>{
    const node=m.get(c.id)!;
    if(!c.parent_id)roots.push(node); else m.get(c.parent_id)?.children.push(node);
  });
  return roots;
}

interface Props {
  store: TMStore;
  navParams?: { tplId?:string; highlightKey?:string };
  onNavigate?: (section: string, params?: unknown) => void;
}

export default function TaskByTypePage({ store, navParams, onNavigate }: Props) {
  const { companies, tasks, fps, taskCategories, taskTemplates, aiPresets, consultations, govPrograms } = store;
  const catTree = useMemo(()=>buildTree(taskCategories),[taskCategories]);

  const [selPath, setSelPath] = useState<string[]>(()=>{
    if(navParams?.tplId){
      const tpl=taskTemplates.find(t=>t.id===navParams.tplId);
      if(tpl?.cat_path?.length) return [tpl.cat_path[0]];
    }
    return catTree[0]?[catTree[0].id]:[];
  });
  const [selTplId, setSelTplId] = useState(navParams?.tplId||'');
  const [selKey, setSelKey] = useState<string|null>(navParams?.highlightKey||null);
  const [catEditOpen, setCatEditOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [compSearch, setCompSearch] = useState('');
  const col1Resize = useResizable({ initialWidth: 260, minWidth: 180, maxWidth: 500 });
  function showToast(msg:string) { setToast(msg); setTimeout(()=>setToast(''),3000); }

  function getCatName(): string {
    let nodes = catTree; const names: string[] = [];
    for (const pid of selPath) { const f = nodes.find(nd => nd.id === pid); if (f) { names.push(f.name); nodes = f.children; } }
    return names[names.length - 1] || '업무';
  }
  async function handleAddCompanyTask(companyId: string, companyName: string) {
    if (selPath.length === 0) return;
    try {
      await api.createTask({
        company_id: companyId,
        type: getCatName(),
        icon: '📋',
        category_path: selPath,
        status: '진행',
        steps: [],
      } as Record<string, unknown>);
      setShowCompanyPicker(false);
      setCompSearch('');
      showToast(companyName + ' — ' + getCatName() + ' 업무 등록 완료');
      await store.refreshTasks();
    } catch (e: unknown) { showToast(e instanceof Error ? e.message : '업무 등록 실패'); }
  }

  const rows = useMemo(()=>{
    if(selPath.length===0)return [];
    const arr:{comp:typeof companies[0];task:typeof tasks[0]}[]=[];
    tasks.filter(t=>{
      const cp=Array.isArray(t.category_path)?t.category_path:[];
      // selPath가 task의 category_path의 prefix인지 확인
      if(cp.length<selPath.length) return false;
      return selPath.every((s,i)=>s===cp[i]);
    }).forEach(task=>{
      const comp=companies.find(c=>c.id===task.company_id);
      if(comp)arr.push({comp,task});
    });
    return arr.sort((a,b)=>(dDayN(a.task.deadline)??999)-(dDayN(b.task.deadline)??999));
  },[companies,tasks,selPath]);

  const selRow = selKey ? rows.find(r=>r.comp.id+':'+r.task.id===selKey) : null;
  const selTpl = taskTemplates.find(t=>t.id===selTplId);

  const [coSearch, setCoSearch] = useState('');
  const viewRows = useMemo(()=>{
    const kw = coSearch.trim().toLowerCase();
    if(!kw) return rows;
    return rows.filter(({comp,task})=>{
      const fp = fps.find(f=>(comp.fps||[]).some(cf=>cf.fp_id===f.id));
      return [comp.name, fp?.name, task.type].filter(Boolean).join(' ').toLowerCase().includes(kw);
    });
  },[rows,coSearch,fps]);

  // ── 카테고리 트리 렌더 ──
  function renderTree(nodes:CatNode[], depth=0):ReactNode[] {
    const items:ReactNode[]=[];
    nodes.forEach(node=>{
      const isInPath=selPath[depth]===node.id;
      const hasChildren=node.children.length>0;
      // 이 노드 하위 업무 수
      const nodeTasks=tasks.filter(t=>{const cp=Array.isArray(t.category_path)?t.category_path:[];return cp.length>depth&&cp[depth]===node.id;}).length;
      const waitingCnt=tasks.filter(t=>{const cp=Array.isArray(t.category_path)?t.category_path:[];return cp.length>depth&&cp[depth]===node.id&&t.status==='FP요청대기';}).length;

      items.push(
        <div key={node.id}
          onClick={()=>{
            const newPath=selPath.slice(0,depth).concat(node.id);
            setSelPath(newPath);
            const directTpls=taskTemplates.filter(t=>{const cp=t.cat_path||[];return cp.length===newPath.length&&cp.every((s,i)=>s===newPath[i]);});
            if(directTpls.length>0){ setSelTplId(directTpls[0].id); }
            else { const subTpls=taskTemplates.filter(t=>{const cp=t.cat_path||[];return cp.length>newPath.length&&newPath.every((s,i)=>s===cp[i]);}); setSelTplId(subTpls.length>0?subTpls[0].id:''); }
            setSelKey(null);
          }}
          style={{paddingLeft:8+depth*14,paddingRight:8,paddingTop:7,paddingBottom:7,cursor:'pointer',
            background:isInPath?C.accentLt:'transparent',
            borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:5}}>
          {hasChildren
            ?<span style={{fontSize:T.xs,color:isInPath?C.accent:C.muted,flexShrink:0}}>{isInPath?'▼':'▶'}</span>
            :<span style={{fontSize:T.xs,color:C.muted,flexShrink:0}}>·</span>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:T.xs,fontWeight:isInPath?700:400,color:isInPath?C.accent:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{node.name}</div>
            {nodeTasks>0&&<div style={{display:'flex',gap:3,marginTop:1}}>
              <span style={{fontSize:T.xs,color:C.muted}}>{nodeTasks}건</span>
              {waitingCnt>0&&<span style={{padding:'1px 4px',borderRadius:3,background:'#F5F3FF',color:C.purple,fontSize:T.xs,fontWeight:700}}>대기{waitingCnt}</span>}
            </div>}
          </div>
        </div>
      );

      if(isInPath){
        if(hasChildren) items.push(...renderTree(node.children, depth+1));
        // 이 경로에 직접 매핑된 템플릿들
        const curPath=selPath.slice(0,depth+1);
        const leafTpls=taskTemplates.filter(t=>{
          const cp=t.cat_path||[];
          return cp.length===curPath.length && cp.every((s,i)=>s===curPath[i]);
        });
        leafTpls.forEach(tpl=>{
          const cnt=tasks.filter(t=>t.template_id===tpl.id).length;
          const waiting=tasks.filter(t=>t.template_id===tpl.id&&t.status==='FP요청대기').length;
          const act=selTplId===tpl.id;
          items.push(
            <div key={tpl.id}
              onClick={e=>{e.stopPropagation();setSelTplId(tpl.id);setSelKey(null);}}
              style={{paddingLeft:8+(depth+1)*14,paddingRight:8,paddingTop:7,paddingBottom:7,
                cursor:'pointer',
                background:act?'rgba(46,117,182,.08)':'transparent',borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:T.xs,fontWeight:act?700:400,color:act?C.accent:C.text}}>{tpl.icon} {tpl.name}</div>
              <div style={{display:'flex',gap:3,marginTop:1}}>
                <span style={{fontSize:T.xs,color:C.muted}}>{cnt}건</span>
                {waiting>0&&<span style={{padding:'1px 4px',borderRadius:3,background:'#F5F3FF',color:C.purple,fontSize:T.xs,fontWeight:700}}>대기{waiting}</span>}
              </div>
            </div>
          );
        });
      }
    });
    return items;
  }

  async function handleAddCategory(name:string){
    try{ await api.createTaskCategory({name,parent_id:null,depth:0,sort_order:0}); await store.refresh(); }
    catch(e:unknown){ showToast('카테고리 추가 실패'); }
  }

  return(
    <div style={{display:'flex',height:'100%',overflow:'hidden'}}>
      {/* 컬럼1: 카테고리 트리 (185px) */}
      <div style={{width:185,borderRight:`1px solid ${C.border}`,background:C.bgAlt,display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
        <div style={{padding:'8px 10px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <span style={{fontSize:T.xs,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.4px'}}>업무 유형</span>
          <button onClick={()=>{if(onNavigate){onNavigate('settings',{tab:'categories'});}else{setCatEditOpen(!catEditOpen);}}}
            style={{fontSize:T.xs,padding:'2px 6px',borderRadius:R.sm,border:`1px solid ${catEditOpen?C.accent:C.border}`,background:catEditOpen?C.accentLt:'transparent',color:catEditOpen?C.accent:C.muted,cursor:'pointer',fontWeight:catEditOpen?700:400}}>
            {catEditOpen&&!onNavigate?'✕ 닫기':'📂 편집'}
          </button>
        </div>
        {catEditOpen&&(
          <div style={{borderBottom:`2px solid ${C.accent}`,background:'#FEFCE8',padding:8,flexShrink:0}}>
            <div style={{fontSize:T.xs,fontWeight:700,color:'#92400E',marginBottom:4}}>새 카테고리 추가</div>
            <input placeholder="카테고리명 (Enter)"
              onKeyDown={e=>{if(e.key==='Enter'&&(e.target as HTMLInputElement).value.trim()){handleAddCategory((e.target as HTMLInputElement).value.trim());(e.target as HTMLInputElement).value='';}}}
              style={{width:'100%',padding:'5px 8px',borderRadius:R.sm,border:`1px solid ${C.accent}`,fontSize:T.xs,outline:'none',boxSizing:'border-box'}}/>
            <div style={{fontSize:T.xs,color:C.muted,marginTop:4}}>세부 구조는 설정 → 카테고리에서 관리</div>
          </div>
        )}
        <div style={{flex:1,overflowY:'auto'}}>
          {catTree.length===0&&<div style={{padding:16,textAlign:'center',color:C.muted,fontSize:T.xs}}>카테고리 없음<br/><span style={{fontSize:T.xs}}>설정에서 추가하세요</span></div>}
          {renderTree(catTree)}

        </div>
      </div>

      {/* 컬럼2: 업체-업무 카드 (240px) */}
      <div style={{width:col1Resize.width,borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bgAlt,flexShrink:0}}>
        <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`,background:C.white,flexShrink:0}}>
          {selPath.length>0?<>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:T.md,fontWeight:800,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{(()=>{const names:string[]=[];let nodes=catTree;for(const pid of selPath){const found=nodes.find(n=>n.id===pid);if(found){names.push(found.name);nodes=found.children;}};return names.join(' > ')||'업무 유형';})()}</div>
              <button onClick={()=>setShowCompanyPicker(true)} style={{fontSize:T.xs,padding:'3px 8px',borderRadius:R.sm,border:'none',background:C.accent,color:'#fff',cursor:'pointer',fontWeight:700,flexShrink:0}}>+ 등록</button>
            </div>
            <div style={{marginTop:7}}>
              <SearchInput value={coSearch} onChange={setCoSearch} placeholder="업체명 검색" style={{padding:'6px 10px',fontSize:T.xs}} />
            </div>
            <div style={{fontSize:T.xs,color:C.muted,marginTop:4}}>{viewRows.length}건{coSearch.trim()?` / 전체 ${rows.length}`:''}</div>
          </>:<div style={{fontSize:T.sm,color:C.muted}}>← 업무 유형을 선택해 주세요</div>}
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'8px 8px'}}>
          {selPath.length===0&&<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:T.sm}}>좌측에서<br/>업무 유형을 선택하세요</div>}
          {selPath.length>0&&rows.length===0&&<Empty icon="📋" message="이 유형의 업무가 없습니다"/>}
          {selPath.length>0&&rows.length>0&&viewRows.length===0&&<Empty icon="🔍" message="검색 결과가 없습니다"/>}
          {viewRows.map(({comp,task})=>{
            const key=comp.id+':'+task.id;
            const act=selKey===key;
            const fp=fps.find(f=>(comp.fps||[]).some(cf=>cf.fp_id===f.id));
            return(
              <div key={key} onClick={()=>setSelKey(act?null:key)}
                style={{padding:'10px 12px',borderRadius:10,border:`2px solid ${act?C.accent:C.border}`,background:act?C.accentLt:C.white,cursor:'pointer',marginBottom:6}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                  <div style={{fontSize:T.sm,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,marginRight:4}}>{comp.name}</div>
                  {task.status==='FP요청대기'
                    ?<Badge bg="#F5F3FF" color={C.purple} style={{fontSize:T.xs}}>대기</Badge>
                    :<DDayBadge deadline={task.deadline}/>}
                </div>
                {task.status!=='FP요청대기'&&<PBar value={task.progress} h={4}/>}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:3}}>
                  <span style={{fontSize:T.xs,color:C.muted}}>{fp?.name||''}</span>
                  {task.status!=='FP요청대기'&&<span style={{fontSize:T.xs,fontWeight:600,color:C.accent}}>{task.progress}%</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ResizeHandle onMouseDown={col1Resize.onMouseDown} />

      {/* 컬럼3: CompanyDetailPanel (업무 탭 선택) */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!selRow?(
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:C.muted,fontSize:T.md,flexDirection:'column',gap:8}}>
            <span style={{fontSize:32}}>👆</span>업체를 선택하면 상세가 표시됩니다
          </div>
        ):(
          <CompanyDetailPanel
            company={selRow.comp}
            tasks={tasks.filter(t=>t.company_id===selRow.comp.id)}
            consultations={consultations.filter(c=>c.company_id===selRow.comp.id)}
            fps={fps}
            govPrograms={govPrograms}
            taskTemplates={taskTemplates}
            taskCategories={taskCategories}
            aiPresets={aiPresets}
            initialTaskId={selRow.task.id}
            onClose={()=>setSelKey(null)}
            onUpdate={async(id,patch)=>{await store.saveCompany(id,patch);}}
            onToast={(m,t)=>showToast(t==='error'?'❌ '+m:'✓ '+m)}
            onRefresh={async()=>{await store.refreshTasks();await store.refreshCompanies();}}
            onNavigateGov={onNavigate?()=>onNavigate('gov'):undefined}
          />
        )}
      </div>
      {showCompanyPicker && selPath.length > 0 && (
        <div style={{position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.4)'}} onClick={()=>{setShowCompanyPicker(false);setCompSearch('');}}/>
          <div style={{position:'relative',width:420,maxHeight:'70vh',borderRadius:12,background:'#fff',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 8px 30px rgba(0,0,0,0.15)'}}>
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:T.md,fontWeight:700}}>업체 선택</div>
                <div style={{fontSize:T.xs,color:C.muted,marginTop:2}}>업무를 등록할 업체를 선택하세요</div>
              </div>
              <button onClick={()=>{setShowCompanyPicker(false);setCompSearch('');}} style={{background:'none',border:'none',fontSize:16,cursor:'pointer',color:C.muted}}>✕</button>
            </div>
            <div style={{padding:'8px 18px'}}>
              <input value={compSearch} onChange={e=>setCompSearch(e.target.value)} placeholder="업체명 검색..."
                style={{width:'100%',padding:'8px 12px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.sm,outline:'none',boxSizing:'border-box'}} autoFocus/>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'0 18px 12px'}}>
              {/* 신규 업체 등록 버튼 */}
              <div onClick={()=>{setShowCompanyPicker(false);setCompSearch('');if(onNavigate)onNavigate('companies',{action:'register'});}}
                style={{padding:'10px 12px',borderRadius:R.sm,border:`2px dashed ${C.accent}44`,marginBottom:8,cursor:'pointer',background:C.accentLt,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}
                onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.borderColor=C.accent;}}
                onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.borderColor=C.accent+'44';}}>
                <span style={{fontSize:14,color:C.accent}}>+</span>
                <span style={{fontSize:T.sm,fontWeight:600,color:C.accent}}>신규 업체 등록</span>
              </div>
              {companies.filter(co=>{
                if(compSearch.trim()){const q=compSearch.trim().toLowerCase();return co.name.toLowerCase().includes(q)||(co.biz_no||'').includes(q)||(co.real_ceo||'').toLowerCase().includes(q)||(co.ceo||'').toLowerCase().includes(q);}
                return true;
              }).slice(0,30).map(co=>(
                  <div key={co.id} onClick={()=>{
                    setShowCompanyPicker(false);setCompSearch('');
                    if(onNavigate) onNavigate('companies',{companyId:co.id,taskId:'_tasks'});
                  }}
                    style={{padding:'10px 12px',borderRadius:R.sm,border:`1px solid ${C.border}`,marginBottom:4,cursor:'pointer',background:'#fff'}}
                    onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=C.accentLt;}}
                    onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background='#fff';}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:T.sm,fontWeight:600}}>{co.name}</span>
                      <span style={{fontSize:T.xs,color:C.accent,fontWeight:600}}>업무 등록 →</span>
                    </div>
                    <div style={{fontSize:T.xs,color:C.muted,marginTop:2}}>{ceoShown(co)}{co.biz_no?' · '+co.biz_no:''}</div>
                  </div>
              ))}
              {companies.filter(co=>{if(compSearch.trim()){const q=compSearch.trim().toLowerCase();return co.name.toLowerCase().includes(q)||(co.biz_no||'').includes(q);}return true;}).length===0&&(
                <div style={{padding:20,textAlign:'center',color:C.muted,fontSize:T.sm}}>검색 결과 없음</div>
              )}
            </div>
          </div>
        </div>
      )}
      {toast&&<div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',padding:'12px 24px',borderRadius:R.md,background:C.text,color:'#fff',fontSize:T.sm,fontWeight:600,zIndex:9999}}>{toast}</div>}
    </div>
  );
}

function dDayN(d:string|null) { if(!d)return null; const _t=new Date(d);_t.setHours(0,0,0,0);const _n=new Date();_n.setHours(0,0,0,0);return Math.round((_t.getTime()-_n.getTime())/86400000); }
